import { useState, useCallback } from "react";
import { Modal } from "antd";
import { priceBackendUrl } from "../../config/gateway";

interface UseUpdatePricesReturn {
  updatePrices: any;
  loading: boolean;
  error: string | null;
  fetchUpdatePrices: () => Promise<void>;
  renderModal: () => JSX.Element;
}

const useUpdatePrices = (): UseUpdatePricesReturn => {
  const [updatePrices, setUpdatePrices] = useState(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [modalMessage, setModalMessage] = useState<string>("");

  const fetchUpdatePrices = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      const tokenStorage = localStorage.getItem("token");
      const token = tokenStorage ? tokenStorage.replace(/['"]+/g, "") : null;

      if (!token) {
        throw new Error(
          "Token de acceso no encontrado. Inicia sesión primero."
        );
      }

      const response = await fetch(
        priceBackendUrl.synchronize(),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response) {
        throw new Error("No se recibió respuesta del servidor.");
      }

      const data = await response.json();
      if (!response.ok) {
        throw new Error(
          data?.message ||
            `Error al obtener el update: ${response.status} - ${response.statusText}`
        );
      }
      if (!data) {
        throw new Error("La respuesta del servidor está vacía.");
      }
      setUpdatePrices(data?.content);
      if (response.status === 200 && data.ok) {
        setModalMessage("Actualización de precios exitosa");
      } else {
        setModalMessage(
          data.message || "Hubo un error en la actualización de precios. Inténtalo nuevamente."
        );
      }
      setIsModalVisible(true);
    } catch (err) {
      if (err instanceof Error && err.name === "AbortError") {
        setError(
          "La solicitud tardó demasiado en responder. Inténtalo de nuevo más tarde."
        );
      } else {
        setError(
          err instanceof Error
            ? err.message
            : "Error desconocido al realizar la solicitud"
        );
      }
      setModalMessage(
        "Hubo un error en la actualización de precios. Inténtalo nuevamente."
      );
      setIsModalVisible(true);
    } finally {
      setLoading(false);
    }
  }, []);

  const renderModal = () => (
    <Modal
      title={
        modalMessage === "Actualización de precios exitosa" ? "Éxito" : "¡Importante!"
      }
      visible={isModalVisible}
      onOk={() => setIsModalVisible(false)}
      onCancel={() => setIsModalVisible(false)}
    >
      <p>{modalMessage}</p>
    </Modal>
  );

  return { fetchUpdatePrices, updatePrices, loading, error, renderModal };
};

export default useUpdatePrices;